import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip, IconButton, CircularProgress, Box } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import SyncIcon from '@mui/icons-material/Sync';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import SettingsIcon from '@mui/icons-material/Settings';
import bugImage from 'assets/bug.png';
import ptzIconInactive from 'assets/ptz-icon-inactive.svg';
import ptzIconActive from 'assets/ptz-icon-active.svg';
import PTZDropdownButton from '../../cameraStream/PTZ/PTZDropdownButton';
import TwoWayAudio from '../../cameraStream/cameraStreamFooter/TwoWayAudio/TwoWayAudio';
import RotateButton from '../../cameraStream/cameraStreamFooter/FooterButtons/RotateButton';
import DayNightModeButton from '../../cameraStream/cameraStreamFooter/FooterButtons/DayNightModeButton';
import OcrSnapshotButton from '../../cameraStream/cameraStreamFooter/FooterButtons/OcrSnapshotButton';
import OcrTextScanIcon from 'components/icons/OcrTextScanIcon';
import PlaybackIcon from 'components/icons/PlaybackIcon';
import FaceMatchingIcon from 'components/icons/FaceMatchingIcon';
import { playbackActions } from 'store/playbackSlice/playbackSlice';
import BrightnessContrastAdjustmentButton from '../../cameraStream/cameraStreamFooter/FooterButtons/BrightnessContrastAdjustment/BrightnessContrastAdjustmentButton';
import { selectPlaybackIsLoading, selectPlaybackIsOpen, selectPlaybackActiveCamId } from 'store/playbackSlice/playbackSelectors';
import { selectFaceMatchIsLoadingForTransaction, selectCurrentMatchTypeForTransaction } from 'store/faceMatchSlice/faceMatchSelectors';
import { ocrVerificationActions } from 'store/ocrVerificationSlice/ocrVerificationSlice';
import { snackbarActions } from 'store/snackbarsSlice/snackbarsSlice';
import { selectOcrInFlightIdentifiersKey, selectOcrVerificationModeByTransactionId } from 'store/ocrVerificationSlice/ocrVerificationSelectors';
import { OCR_IDENTIFIER_LABEL, getOcrMenuOptions } from 'utils/ocrVerification';
import {
    OCR_CAPTURE_TIMEOUT_MS,
    STREAM_FOOTER_ICON_STYLE,
    STREAM_FOOTER_MENU_ICON_STYLE,
} from 'components/transaction/components/panels/transaction/cameras/MediaSection/constants';

const FACE_MATCH_CAMERA_TYPES = ['CAR_FACE', 'CAR_ID', 'TRUCK_FACE', 'TRUCK_ID'];
const NO_OCR_OPTIONS = [];

const createOcrAttemptId = () => globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const useCameraFooterActions = ({
    isPTZCamera,
    videoWallCameras,
    togglePTZ,
    isPTZOn,
    fullZoomIn,
    fullZoomOut,
    ptzRequestStatus,
    isCameraFocused,
    isAudioAvailable,
    audioEnabled,
    isCameraActive,
    onToggleAudio,
    enableDemoAudioFile,
    camVideoId,
    camId,
    camConfig,
    isTransactionPage,
    isDisabledSnapshot,
    onTakeSnapshot,
    onFaceMatch,
    rotation,
    expandedCamId,
    isMtxDirect,
    refreshVideoStreamHandler,
    hardRefreshVideoStreamHandler,
    isFullScreenRequested,
    openDialogHandler,
    footerMode,
    hasCameraIssues,
    clickHandlerCameraBugIcon,
    footerItemsStyle,
    isCameraFreezed,
    snapshotTooltipTitle,
    transactionId,
    visibleCameraIds,
    openWindowVideoIdOutput,
    isStreamTestPage,
}) => {
    const [isSnapshotLoading, setIsSnapshotLoading] = useState(false);
    const [isFaceMatchActionLoading, setIsFaceMatchActionLoading] = useState(false);
    const [isOcrCapturing, setIsOcrCapturing] = useState(false);
    const ocrCaptureTimerRef = useRef(null);
    const isCameraOutOfFocus = isPTZOn && !isCameraFocused;

    const dispatch = useDispatch();
    const isPlaybackLoading = useSelector(selectPlaybackIsLoading);
    const isPlaybackOpen = useSelector(selectPlaybackIsOpen);
    const playbackActiveCamId = useSelector(selectPlaybackActiveCamId);
    const isFaceMatchLoading = useSelector(selectFaceMatchIsLoadingForTransaction(transactionId));
    const currentMatchType = useSelector(selectCurrentMatchTypeForTransaction(transactionId));
    const isCameraExpanded = expandedCamId === camConfig?.id;

    const ocrModeSelector = useMemo(() => selectOcrVerificationModeByTransactionId(transactionId), [transactionId]);
    const ocrMode = useSelector(ocrModeSelector);
    const ocrOptions = useMemo(
        () => (isTransactionPage ? getOcrMenuOptions(camConfig?.type, ocrMode) : NO_OCR_OPTIONS),
        [isTransactionPage, camConfig?.type, ocrMode]
    );
    const hasOcrAction = ocrOptions.length > 0;
    const ocrInFlightKey = useSelector(selectOcrInFlightIdentifiersKey(hasOcrAction ? transactionId : null));
    const ocrInFlightIdentifiers = useMemo(() => (ocrInFlightKey ? ocrInFlightKey.split(',') : NO_OCR_OPTIONS), [ocrInFlightKey]);

    const isFaceMatchPairInProgress = useMemo(() => {
        if (!isFaceMatchLoading || !currentMatchType) return false;
        const cameraType = camConfig?.type;
        if (!cameraType || !FACE_MATCH_CAMERA_TYPES.includes(cameraType)) return false;

        const isCar = cameraType === 'CAR_FACE' || cameraType === 'CAR_ID';
        const isTruck = cameraType === 'TRUCK_FACE' || cameraType === 'TRUCK_ID';

        return (isCar && currentMatchType === 'CAR') || (isTruck && currentMatchType === 'TRUCK');
    }, [isFaceMatchLoading, currentMatchType, camConfig?.type]);

    const cameraState = useMemo(
        () => ({
            isPTZCamera,
            isCameraActive,
            isCameraFreezed,
            isPTZOn,
            isCameraOutOfFocus,
            audioEnabled,
            isAudioAvailable,
            isDisabledSnapshot,
        }),
        [isPTZCamera, isCameraActive, isCameraFreezed, isPTZOn, isCameraOutOfFocus, audioEnabled, isAudioAvailable, isDisabledSnapshot]
    );

    const cameraConfig = useMemo(
        () => ({
            camId: camConfig?.id,
            cameraName: camConfig?.name,
            hasExtension: !!camConfig?.extensionNumber3CX,
            hasTalkUrl: !!camConfig?.talkUrl,
            recordingUrl: camConfig?.recordingUrl || '',
            recordingPath: camConfig?.recordingPath || '',
        }),
        [camConfig?.id, camConfig?.name, camConfig?.extensionNumber3CX, camConfig?.talkUrl, camConfig?.recordingUrl, camConfig?.recordingPath]
    );

    const flags = useMemo(
        () => ({
            videoWallCameras,
            isTransactionPage,
            isMtxDirect,
            isFullScreenRequested,
            footerMode,
            hasCameraIssues,
            openWindowVideoIdOutput,
        }),
        [videoWallCameras, isTransactionPage, isMtxDirect, isFullScreenRequested, footerMode, hasCameraIssues, openWindowVideoIdOutput]
    );

    const createPTZAction = useCallback(() => {
        if (!cameraState.isPTZCamera || cameraState.isCameraFreezed) return null;

        return {
            key: 'ptz',
            component: (
                <PTZDropdownButton
                    isPTZOn={cameraState.isPTZOn}
                    togglePTZ={togglePTZ}
                    fullZoomIn={fullZoomIn}
                    fullZoomOut={fullZoomOut}
                    ptzRequestStatus={ptzRequestStatus}
                    isCameraOutOfFocus={cameraState.isCameraOutOfFocus}
                />
            ),
            menuOption: {
                label: 'PTZ Control',
                onClick: togglePTZ,
                icon: <img src={cameraState.isPTZOn ? ptzIconActive : ptzIconInactive} style={STREAM_FOOTER_MENU_ICON_STYLE} />,
            },
        };
    }, [
        cameraState.isPTZCamera,
        cameraState.isPTZOn,
        cameraState.isCameraOutOfFocus,
        cameraState.isCameraFreezed,
        togglePTZ,
        fullZoomIn,
        fullZoomOut,
        ptzRequestStatus,
    ]);

    const createTalkAction = useCallback(() => {
        if (!cameraState.isCameraActive || (!cameraConfig.hasExtension && !cameraConfig.hasTalkUrl)) return null;

        return {
            key: 'talk',
            component: (
                <TwoWayAudio
                    onToggleAudio={onToggleAudio}
                    enableDemoAudioFile={enableDemoAudioFile}
                    camVideoId={camVideoId}
                    camId={camId}
                    camConfig={camConfig}
                    className={footerItemsStyle}
                    transactionId={transactionId}
                />
            ),
            isWrapper: true,
        };
    }, [
        cameraState.isCameraActive,
        cameraConfig.hasExtension,
        cameraConfig.hasTalkUrl,
        onToggleAudio,
        enableDemoAudioFile,
        camVideoId,
        camId,
        camConfig,
        footerItemsStyle,
        transactionId,
    ]);

    const createAudioAction = useCallback(() => {
        if (!cameraState.isAudioAvailable || !cameraState.isCameraActive || cameraState.isCameraFreezed) return null;

        const isVolumeUp = cameraState.audioEnabled;
        const Icon = isVolumeUp ? VolumeUpIcon : VolumeOffIcon;
        const title = isVolumeUp ? 'Turn off stream sound [Ctrl+M]' : 'Turn on stream sound [Ctrl+M]';
        const label = isVolumeUp ? 'Mute stream sound' : 'Unmute stream sound';

        return {
            key: 'audio',
            component: (
                <Tooltip title={title}>
                    <Icon fontSize="small" style={STREAM_FOOTER_ICON_STYLE} onClick={onToggleAudio} />
                </Tooltip>
            ),
            menuOption: {
                label,
                onClick: onToggleAudio,
                icon: <Icon fontSize="small" style={STREAM_FOOTER_MENU_ICON_STYLE} />,
            },
        };
    }, [cameraState.isAudioAvailable, cameraState.isCameraActive, cameraState.isCameraFreezed, cameraState.audioEnabled, onToggleAudio]);

    const handleSnapshotClick = useCallback(
        async (e) => {
            if (isSnapshotLoading || cameraState.isDisabledSnapshot) return;
            if (e) e.stopPropagation();

            const downloadImmediately = !!(e?.ctrlKey || e?.metaKey);
            setIsSnapshotLoading(true);
            onTakeSnapshot({
                onComplete: () => {
                    setIsSnapshotLoading(false);
                },
                downloadImmediately,
            });
        },
        [isSnapshotLoading, cameraState.isDisabledSnapshot, onTakeSnapshot]
    );

    useEffect(() => () => clearTimeout(ocrCaptureTimerRef.current), []);

    // A new frame is captured for every read; regular snapshots never start one.
    const handleOcrExtract = useCallback(
        (identifier) => {
            if (isOcrCapturing || cameraState.isDisabledSnapshot || ocrInFlightIdentifiers.includes(identifier)) return;
            const notifyNotCaptured = () =>
                dispatch(
                    snackbarActions.enqueueSnackbar({
                        message: 'Get text from photo: the camera frame could not be captured. Try again.',
                        options: { variant: 'error' },
                    })
                );
            setIsOcrCapturing(true);
            clearTimeout(ocrCaptureTimerRef.current);
            ocrCaptureTimerRef.current = setTimeout(() => {
                console.warn(`handleOcrExtract(): transactionId=${transactionId}, identifier=${identifier} - the capture never completed`);
                setIsOcrCapturing(false);
                notifyNotCaptured();
            }, OCR_CAPTURE_TIMEOUT_MS);
            onTakeSnapshot({
                namePrefix: OCR_IDENTIFIER_LABEL[identifier],
                onSaved: ({ snapshotId, cameraName, captured }) => {
                    if (!captured) {
                        notifyNotCaptured();
                        return;
                    }
                    dispatch(
                        ocrVerificationActions.extractionRequested({
                            transactionId,
                            attemptId: createOcrAttemptId(),
                            identifier,
                            snapshotId,
                            cameraName,
                            cameraType: camConfig?.type,
                            requestedAt: Date.now(),
                        })
                    );
                },
                onComplete: () => {
                    clearTimeout(ocrCaptureTimerRef.current);
                    setIsOcrCapturing(false);
                },
            });
        },
        [isOcrCapturing, cameraState.isDisabledSnapshot, ocrInFlightIdentifiers, onTakeSnapshot, dispatch, transactionId, camConfig?.type]
    );

    const handleFaceMatchClick = useCallback(async () => {
        // One request at a time per transaction, whichever camera pair (BEAST-13916 UC #1 AC #4): a second one
        // cancels the first. Checked here because the overflow menu entry has no disabled state.
        if (isFaceMatchLoading || isFaceMatchActionLoading || cameraState.isDisabledSnapshot) return;

        setIsFaceMatchActionLoading(true);

        if (onFaceMatch) {
            const faceMatchStarted = await onFaceMatch();
            if (faceMatchStarted === false) {
                setIsFaceMatchActionLoading(false);
            }
        } else {
            setIsFaceMatchActionLoading(false);
        }
    }, [isFaceMatchLoading, isFaceMatchActionLoading, cameraState.isDisabledSnapshot, onFaceMatch]);

    useEffect(() => {
        if (!isFaceMatchLoading && isFaceMatchActionLoading) {
            setIsFaceMatchActionLoading(false);
        }
    }, [isFaceMatchLoading, isFaceMatchActionLoading]);

    const createOcrSnapshotAction = useCallback(() => {
        const [defaultIdentifier] = ocrOptions;
        const tooltip = cameraState.isDisabledSnapshot
            ? "Can't snapshot loading cameras"
            : `Get text from photo: ${OCR_IDENTIFIER_LABEL[defaultIdentifier]}`;
        const menuIcon = (
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CameraAltIcon fontSize="small" style={STREAM_FOOTER_MENU_ICON_STYLE} />
                <Box sx={{ position: 'absolute', bottom: -1, right: -5, display: 'flex' }}>
                    <OcrTextScanIcon />
                </Box>
            </Box>
        );

        return {
            key: 'ocr-screenshot',
            component: (
                <OcrSnapshotButton
                    options={ocrOptions}
                    inFlightIdentifiers={ocrInFlightIdentifiers}
                    disabled={cameraState.isDisabledSnapshot || isOcrCapturing}
                    tooltip={tooltip}
                    onExtract={handleOcrExtract}
                />
            ),
            // the overflow menu has no sub-menu: it runs the default read
            menuOption: {
                label: 'Get text from photo',
                subtitle: OCR_IDENTIFIER_LABEL[defaultIdentifier],
                onClick: () => handleOcrExtract(defaultIdentifier),
                icon: menuIcon,
            },
        };
    }, [ocrOptions, ocrInFlightIdentifiers, cameraState.isDisabledSnapshot, isOcrCapturing, handleOcrExtract]);

    const createScreenshotAction = useCallback(() => {
        if (!cameraState.isCameraActive || !flags.isTransactionPage || cameraState.isCameraFreezed) return null;
        // On a gate with OCR verification, cameras that read numbers take the snapshot slot with "Get text from photo".
        if (hasOcrAction) return createOcrSnapshotAction();

        const isDisabled = cameraState.isDisabledSnapshot || isSnapshotLoading;
        const tooltipTitle = cameraState.isDisabledSnapshot ? "Can't snapshot loading cameras" : snapshotTooltipTitle;

        return {
            key: 'screenshot',
            component: (
                <Tooltip placement="bottom" title={tooltipTitle} disableInteractive>
                    <span>
                        <IconButton
                            size="small"
                            onClick={handleSnapshotClick}
                            disabled={isDisabled}
                            sx={{
                                padding: '4px',
                                position: 'relative',
                                '&.Mui-disabled': {
                                    opacity: 0.5,
                                },
                            }}
                        >
                            <CameraAltIcon
                                fontSize="small"
                                style={{
                                    ...STREAM_FOOTER_ICON_STYLE,
                                    color: '#fff',
                                    opacity: isSnapshotLoading ? 0.3 : 1,
                                    transition: 'opacity 0.15s ease',
                                }}
                            />
                            {isSnapshotLoading && (
                                <CircularProgress
                                    size={16}
                                    sx={{
                                        color: '#2196f3',
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        marginTop: '-0.5rem',
                                        marginLeft: '-0.5rem',
                                    }}
                                />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>
            ),
            menuOption: {
                label: 'Take screenshot',
                onClick: handleSnapshotClick,
                icon: <CameraAltIcon fontSize="small" style={STREAM_FOOTER_MENU_ICON_STYLE} />,
            },
        };
    }, [
        cameraState.isCameraActive,
        cameraState.isCameraFreezed,
        cameraState.isDisabledSnapshot,
        flags.isTransactionPage,
        snapshotTooltipTitle,
        handleSnapshotClick,
        isSnapshotLoading,
        hasOcrAction,
        createOcrSnapshotAction,
    ]);

    const createFaceMatchAction = useCallback(() => {
        if (!cameraState.isCameraActive || !flags.isTransactionPage || cameraState.isCameraFreezed) return null;

        const isFaceMatchCamera = FACE_MATCH_CAMERA_TYPES.includes(camConfig?.type);
        if (!isFaceMatchCamera) return null;

        // Started from another camera of this transaction, of either pair.
        const isOtherCameraProcessing = isFaceMatchLoading && !isFaceMatchActionLoading;
        const isDisabled = cameraState.isDisabledSnapshot || isFaceMatchActionLoading || isOtherCameraProcessing;

        let tooltipTitle;
        if (cameraState.isDisabledSnapshot) {
            tooltipTitle = "Can't run face match on loading cameras";
        } else if (isOtherCameraProcessing) {
            tooltipTitle = isFaceMatchPairInProgress ? 'Face match in progress on paired camera' : 'Face match in progress on the other camera pair';
        } else {
            tooltipTitle = (
                <span>
                    Snapshot both cameras
                    <br />
                    and run a face match
                </span>
            );
        }

        return {
            key: 'face-match',
            component: (
                <Tooltip placement="top" title={tooltipTitle}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={handleFaceMatchClick}
                            disabled={isDisabled}
                            sx={{
                                padding: '4px',
                                position: 'relative',
                                '&.Mui-disabled': {
                                    opacity: 0.5,
                                },
                            }}
                        >
                            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                <CameraAltIcon
                                    fontSize="small"
                                    style={{
                                        ...STREAM_FOOTER_ICON_STYLE,
                                        color: '#fff',
                                        opacity: isFaceMatchActionLoading ? 0.3 : 1,
                                        transition: 'opacity 0.15s ease',
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: -2,
                                        right: -4,
                                        width: 15,
                                        height: 15,
                                        borderRadius: '50%',
                                        backgroundColor: '#2196f3',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1.5px solid #2196f3',
                                    }}
                                >
                                    <FaceMatchingIcon />
                                </Box>
                            </Box>
                            {isFaceMatchActionLoading && (
                                <CircularProgress
                                    size={16}
                                    sx={{
                                        color: '#2196f3',
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        marginTop: '-0.5rem',
                                        marginLeft: '-0.5rem',
                                    }}
                                />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>
            ),
            menuOption: {
                label: 'Face match',
                onClick: handleFaceMatchClick,
                icon: (
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                        <CameraAltIcon fontSize="small" style={STREAM_FOOTER_MENU_ICON_STYLE} />
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: -2,
                                right: -4,
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: '#2196f3',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <FaceMatchingIcon />
                        </Box>
                    </Box>
                ),
            },
        };
    }, [
        cameraState.isCameraActive,
        cameraState.isCameraFreezed,
        cameraState.isDisabledSnapshot,
        flags.isTransactionPage,
        handleFaceMatchClick,
        isFaceMatchLoading,
        isFaceMatchActionLoading,
        isFaceMatchPairInProgress,
        camConfig?.type,
    ]);

    const dayNightModeButtonAction = useCallback(() => {
        if (camConfig.type !== 'BOL') return null;

        return {
            key: 'dayNight',
            component: <DayNightModeButton camId={camConfig.camId} />,
            menuOption: {
                onClick: () => {},
                icon: <DayNightModeButton camId={camConfig.camId} />,
            },
        };
    }, [camConfig.camId, camConfig.type]);

    const brightnessContrastButtonAction = useCallback(() => {
        if (!isStreamTestPage) {
            return {
                key: 'brightnessContrast',
                component: (
                    <BrightnessContrastAdjustmentButton
                        camId={camConfig.camId}
                        visibleCameraIds={visibleCameraIds}
                        videoWallCameras={videoWallCameras}
                        flags={flags}
                    />
                ),
                menuOption: {
                    onClick: () => {},
                    icon: (
                        <BrightnessContrastAdjustmentButton
                            camId={camConfig.camId}
                            visibleCameraIds={visibleCameraIds}
                            videoWallCameras={videoWallCameras}
                            flags={flags}
                        />
                    ),
                },
            };
        }
        // eslint-disable-next-line
    }, [camConfig.camId, camConfig.type]);

    const createRotateAction = useCallback(() => {
        if (flags.videoWallCameras || cameraState.isCameraFreezed) return null;

        return {
            key: 'rotate',
            component: (
                <RotateButton
                    rotation={rotation}
                    style={STREAM_FOOTER_ICON_STYLE}
                    onVideoWall={flags.videoWallCameras}
                    isExpanded={expandedCamId === cameraConfig.camId}
                />
            ),
            menuOption: {
                label: 'Rotate camera',
                onClick: () => {},
                icon: <RotateButton rotation={0} style={STREAM_FOOTER_MENU_ICON_STYLE} onVideoWall={false} isExpanded={false} />,
            },
        };
    }, [flags.videoWallCameras, cameraState.isCameraFreezed, rotation, expandedCamId, cameraConfig.camId]);

    const handlePlaybackClick = useCallback(() => {
        if (isPlaybackLoading) return;
        dispatch(
            playbackActions.openPlayback({
                camId: cameraConfig.camId,
                cameraName: cameraConfig.cameraName,
                transactionId,
                recordingUrl: cameraConfig.recordingUrl,
                recordingPath: cameraConfig.recordingPath,
            })
        );
    }, [
        isPlaybackLoading,
        dispatch,
        cameraConfig.camId,
        cameraConfig.cameraName,
        cameraConfig.recordingUrl,
        cameraConfig.recordingPath,
        transactionId,
    ]);

    const createPlaybackAction = useCallback(() => {
        if (flags.videoWallCameras || cameraState.isCameraFreezed || !cameraConfig.recordingUrl) return null;

        const isActiveForThisCamera = isPlaybackOpen && playbackActiveCamId === cameraConfig.camId;

        return {
            key: 'playback',
            component: (
                <Tooltip title="Playback">
                    <IconButton
                        size="small"
                        onClick={handlePlaybackClick}
                        sx={{
                            padding: '4px',
                            backgroundColor: isActiveForThisCamera ? 'white' : 'transparent',
                            borderRadius: '4px',
                            '&:hover': {
                                backgroundColor: isActiveForThisCamera ? 'white' : 'rgba(255, 255, 255, 0.1)',
                            },
                        }}
                    >
                        <PlaybackIcon color={isActiveForThisCamera ? '#1976d2' : 'white'} />
                    </IconButton>
                </Tooltip>
            ),
            menuOption: {
                label: 'Playback',
                onClick: handlePlaybackClick,
                icon: <PlaybackIcon color={isActiveForThisCamera ? '#1976d2' : 'white'} />,
            },
        };
    }, [
        handlePlaybackClick,
        flags.videoWallCameras,
        cameraState.isCameraFreezed,
        isPlaybackOpen,
        playbackActiveCamId,
        cameraConfig.camId,
        cameraConfig.recordingUrl,
    ]);

    const createRefreshActions = useCallback(() => {
        if (!cameraState.isCameraActive) return [];

        const actions = [];

        actions.push({
            key: 'soft-refresh',
            component: (
                <Tooltip title={flags.isMtxDirect ? 'Refresh stream' : 'Soft refresh stream'}>
                    <SyncIcon fontSize="small" style={{ ...STREAM_FOOTER_ICON_STYLE, color: 'white' }} onClick={refreshVideoStreamHandler} />
                </Tooltip>
            ),
            menuOption: {
                label: flags.isMtxDirect ? 'Refresh stream' : 'Soft refresh stream',
                onClick: refreshVideoStreamHandler,
                icon: <SyncIcon fontSize="small" style={STREAM_FOOTER_MENU_ICON_STYLE} />,
            },
        });

        if (!flags.isMtxDirect) {
            actions.push({
                key: 'hard-refresh',
                component: (
                    <Tooltip title="Hard refresh stream">
                        <SyncProblemIcon
                            fontSize="small"
                            style={{ ...STREAM_FOOTER_ICON_STYLE, color: 'white' }}
                            onClick={hardRefreshVideoStreamHandler}
                        />
                    </Tooltip>
                ),
                menuOption: {
                    label: 'Hard refresh stream',
                    subtitle: 'Applies to all systems (HIK, AXIS, AIQ)',
                    onClick: hardRefreshVideoStreamHandler,
                    icon: <SyncProblemIcon fontSize="small" style={STREAM_FOOTER_MENU_ICON_STYLE} />,
                },
            });
        }

        return actions;
    }, [cameraState.isCameraActive, flags.isMtxDirect, refreshVideoStreamHandler, hardRefreshVideoStreamHandler]);

    const allActions = useMemo(() => {
        const actions = [];

        const playbackAction = createPlaybackAction();
        if (playbackAction) {
            actions.push(playbackAction);
        }

        const actionCreators = [
            createPTZAction,
            dayNightModeButtonAction,
            createTalkAction,
            createAudioAction,
            createScreenshotAction,
            brightnessContrastButtonAction,
            createFaceMatchAction,
            createRotateAction,
        ];
        const otherActions = actionCreators.map((creator) => creator()).filter(Boolean);
        actions.push(...otherActions);

        const currentCount = actions.length;
        const refreshActions = createRefreshActions();
        const refreshCount = refreshActions.length;

        if (isCameraExpanded || currentCount + refreshCount <= 5) {
            actions.push(...refreshActions);
        }

        return actions;
    }, [
        createPlaybackAction,
        createPTZAction,
        dayNightModeButtonAction,
        createTalkAction,
        createAudioAction,
        createScreenshotAction,
        brightnessContrastButtonAction,
        createFaceMatchAction,
        createRotateAction,
        createRefreshActions,
        isCameraExpanded,
    ]);

    const frozenCameraActions = useMemo(() => {
        if (!cameraState.isCameraFreezed) return null;
        const actions = [];
        const talkAction = createTalkAction();
        if (talkAction) {
            actions.push(talkAction);
        }
        actions.push(...createRefreshActions());

        if (flags.hasCameraIssues) {
            actions.push({
                key: 'report-issue',
                component: (
                    <Tooltip title="Report Issue">
                        <img src={bugImage} style={STREAM_FOOTER_ICON_STYLE} alt="Report Issue" onClick={() => clickHandlerCameraBugIcon(true)} />
                    </Tooltip>
                ),
            });
        }

        return actions;
    }, [cameraState.isCameraFreezed, createTalkAction, createRefreshActions, flags.hasCameraIssues, clickHandlerCameraBugIcon]);

    const menuOptions = useMemo(() => {
        const actions = cameraState.isCameraFreezed ? frozenCameraActions : allActions;
        const visibleActionCount = isCameraExpanded ? actions?.length : 5;
        const visibleActions = actions?.slice(0, visibleActionCount) || [];
        const hiddenActions = actions?.slice(visibleActionCount) || [];
        const options = [];

        hiddenActions.forEach((action) => {
            if (action.menuOption && !action.isWrapper) {
                options.push(action.menuOption);
            }
        });
        if (!cameraState.isCameraFreezed && cameraState.isCameraActive) {
            const hasRefreshInVisible = visibleActions.some((action) => action.key === 'soft-refresh' || action.key === 'hard-refresh');

            if (!hasRefreshInVisible) {
                const refreshOptions = createRefreshActions().map((action) => action.menuOption);
                options.push(...refreshOptions);
            }
        }

        if (!flags.isFullScreenRequested) {
            options.push({
                label: 'Camera info and debug',
                onClick: openDialogHandler,
                icon: <SettingsIcon fontSize="small" style={STREAM_FOOTER_MENU_ICON_STYLE} />,
            });
        }

        if (!flags.footerMode && flags.hasCameraIssues && cameraState.isCameraActive) {
            const hasReportIssueInVisible = cameraState.isCameraFreezed
                ? frozenCameraActions?.some((action) => action.key === 'report-issue')
                : false;

            if (!hasReportIssueInVisible) {
                options.push({
                    label: 'Report Issue',
                    onClick: () => clickHandlerCameraBugIcon(true),
                    icon: <img src={bugImage} style={STREAM_FOOTER_MENU_ICON_STYLE} />,
                });
            }
        }

        return options.filter(Boolean);
    }, [
        cameraState.isCameraFreezed,
        cameraState.isCameraActive,
        frozenCameraActions,
        allActions,
        createRefreshActions,
        flags.isFullScreenRequested,
        flags.footerMode,
        flags.hasCameraIssues,
        openDialogHandler,
        clickHandlerCameraBugIcon,
        isCameraExpanded,
    ]);

    const actionsToRender = cameraState.isCameraFreezed ? frozenCameraActions : allActions;
    const visibleActions = actionsToRender?.slice(0, isCameraExpanded ? actionsToRender.length : 5) || [];

    return {
        visibleActions,
        menuOptions,
    };
};

export default useCameraFooterActions;
